import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import '../app.css';

const Hero = () => {
    const animationRef = useRef(null);
    const headingRef = useRef(null);
    const paragraphRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-fade-in');
                        entry.target.classList.remove('opacity-0');
                    } else {
                        entry.target.classList.remove('animate-fade-in');
                        entry.target.classList.add('opacity-0');
                    }
                });
            },
            { threshold: 0.1 }
        );

        const currentAnimationRef = animationRef.current;
        const currentHeadingRef = headingRef.current;
        const currentParagraphRef = paragraphRef.current;
        const currentButtonRef = buttonRef.current;

        if (currentAnimationRef) {
            observer.observe(currentAnimationRef);
        }

        if (currentHeadingRef) {
            observer.observe(currentHeadingRef);
        }

        if (currentParagraphRef) {
            observer.observe(currentParagraphRef);
        }

        if (currentButtonRef) {
            observer.observe(currentButtonRef);
        }

        return () => {
            if (currentAnimationRef) {
                observer.unobserve(currentAnimationRef);
            }
            if (currentHeadingRef) {
                observer.unobserve(currentHeadingRef);
            }
            if (currentParagraphRef) {
                observer.unobserve(currentParagraphRef);
            }
            if (currentButtonRef) {
                observer.unobserve(currentButtonRef);
            }
        };
    }, []);

    const features = [
        {
            title: "Inventory Management",
            icon: "📦",
            description: "Real-time stock tracking, automatic reorder notifications, and detailed inventory analytics."
        },
        {
            title: "Sales Processing",
            icon: "💰",
            description: "Complete point-of-sale functionality with customer management and performance tracking."
        },
        {
            title: "Expense Tracking",
            icon: "📊",
            description: "Categorized expense logging, budget management, and financial report generation."
        },
        {
            title: "Payment Processing",
            icon: "💳",
            description: "Secure transaction processing supporting multiple payment methods and reconciliation."
        },
        {
            title: "Advanced Calculations",
            icon: "🧮",
            description: "From basic arithmetic to complex financial calculations and customizable templates."
        }
    ];

    return (
        <>
            <section id="hero" className="relative pt-36 pb-16 px-12 bg-white lg:min-h-screen flex items-center">
                <img
                    src="assets/bg-sample.jpg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-20 z-1"
                />
                <div className="container mx-auto px-4 grid md:grid-cols-2 gap-8 items-center justify-center">
                    <div className="text-center md:text-left">
                        <h3
                            ref={headingRef}
                            className="text-2xl md:text-3xl lg:text-4xl font-bold leading-relaxed text-primary mb-4"

                        >
                            A single device that automates accounting, tracks payments, and simplifies tax calculations.
                        </h3>

                        <button
                            ref={buttonRef}
                            className="bg-secondary text-white px-8 py-3 rounded-full text-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg opacity-0 delay-500 flex items-center"
                        >
                            Download App
                            <ArrowRight className="ml-2 animate-pulse" size={18} />
                        </button>
                    </div>

                    <div>
                        <img
                            ref={animationRef}
                            src="/assets/Calculator.svg"
                            alt="Hero"
                            className="w-[50%] opacity-80 h-auto ml-24 animate-fade-in opacity-0 transform transition-all duration-1000 ease-out delay-300 hover:scale-110"
                        />
                    </div>
                </div>
            </section>
            <div className="container mx-auto pt-16 px-4 lg:min-h-screen">
                <h2 className="text-3xl font-bold text-center text-primary mb-12">Key Features</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition duration-300 border border-gray-100 hover:border-indigo-200"
                        >
                            <div className="text-4xl mb-4">{feature.icon}</div>
                            <h3 className="text-xl font-bold mb-2 text-primary">{feature.title}</h3>
                            <p className="text-gray-600">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default Hero;